import { AfterViewInit, Directive, Host, Inject, Input, OnDestroy, Self } from '@angular/core';
import { MatSort, MatSortable, Sort, SortDirection } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { LocalstorageRepoService } from '@dash/randy/shared/classes/localstorage-repo.service';
import {
  SELECTED_APPLICATION_ID,
  SelectedApplicationId,
} from '@dash/randy/tokens/selected-application-id.token';

@Directive({
  selector: '[matSort][persistSort]',
  standalone: true,
})
export class PersistSortDirective implements AfterViewInit, OnDestroy {
  @Input('persistSort') storageKey!: string;

  private subscription?: Subscription;

  constructor(
    @Host() @Self() private readonly matSort: MatSort,
    private readonly localStorageService: LocalstorageRepoService,
    @Inject(SELECTED_APPLICATION_ID) private readonly selectedApplicationId: SelectedApplicationId,
  ) {}

  ngAfterViewInit(): void {
    this.matSort.disableClear = true;

    const storedSort = this.getStoredSort();
    const defaultSortable = this.getDefaultSortable();
    const active = this.isSortable(storedSort?.active)
      ? storedSort.active
      : this.isSortable(this.matSort.active)
        ? this.matSort.active
        : defaultSortable?.id;

    if (active) {
      this.matSort.active = active;
      this.matSort.direction = this.getDirection(storedSort, defaultSortable);
      this.matSort.sortChange.emit({ active: this.matSort.active, direction: this.matSort.direction });
    }

    this.subscription = this.matSort.sortChange.subscribe(sort => this.saveSort(sort));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private get storageId(): string {
    return `${this.selectedApplicationId() ?? 'global'}:${this.storageKey}:sort`;
  }

  private getStoredSort(): Sort | null {
    try {
      const stored = this.localStorageService.getItem(this.storageId, localStorage);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private saveSort(sort: Sort): void {
    this.localStorageService.setItem(this.storageId, JSON.stringify(sort), localStorage);
  }

  private getDefaultSortable(): MatSortable | undefined {
    return this.matSort.sortables.values().next().value;
  }

  private isSortable(id: string | undefined): id is string {
    return !!id && this.matSort.sortables.has(id);
  }

  private getDirection(storedSort: Sort | null, defaultSortable: MatSortable | undefined): SortDirection {
    if (storedSort?.direction === 'asc' || storedSort?.direction === 'desc') {
      return storedSort.direction;
    }
    if (this.matSort.direction === 'asc' || this.matSort.direction === 'desc') {
      return this.matSort.direction;
    }
    return defaultSortable?.start || this.matSort.start;
  }
}
